import { datadogRum } from "@datadog/browser-rum";
import { useCallback, useEffect, useRef, useState } from "react";

import { EhrConnectionStatus, useEhrConnectionQuery, useImportClientListMutation } from "@api/graphql/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";
import { useOrgSessionAction } from "@hooks/useOrgSessionAction";

const CLIENT_IMPORT_INITIALIZATION_DELAY_MS = 5000;
const EHR_CONNECTION_POLLING_INTERVAL_MS = 3000;
const MAX_POLLING_DURATION_MS = 120_000;
// Number of consecutive ConnectionError polls required before treating it as terminal.
// The Temporal worker can briefly flip status to ConnectionError between
// motivity_get_clients_activity retry attempts; without this debounce a single
// transient observation fires a false error toast even though attempt 2 ultimately
// succeeds. Two polls (≈ 6s) is enough to ride out a single retry boundary.
const CONNECTION_ERROR_CONFIRMATION_POLLS = 2;
// Same shape, opposite end: require two consecutive Connected polls before
// treating an ImportingClientList → Connected transition as the terminal
// success state. The worker can also briefly flip status to Connected between
// retry attempts (the symmetric Motivity flap that motivated the success
// debounce in EHRConnectionProvider). Without this guard, the first transient
// Connected after Importing fires onSuccess immediately — for CR/Refresh
// consumers that toast on onSuccess, the user sees "Import completed" before
// the workflow actually finishes.
const CONNECTED_CONFIRMATION_POLLS = 2;

// INT-36 org-session import. CentralReach orgs on shared credentials
// queue an EhrActionRequest instead of signalling a per-provider workflow,
// so completion is read off the request row rather than
// ehrConnection.status — the org session never writes that status, and the
// transition-based poll below would otherwise settle on "connected"
// immediately and report an import that never ran.
interface UseClientListImportOptions {
  enabled?: boolean;
  errorMessage?: string;
  waitForStatusTransition?: boolean;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
  onLoginFailed?: () => void;
  /** Managed-onboarding gate: false until Frontera has cleared this tenant's
   * EHR connection, or the integration is graduated. Checked as the very
   * first thing `startImport` does — no network call reaches the EHR while
   * false, regardless of what triggered the call (a manual click or an
   * automatic sync-on-connect). Defaults to true so existing callers that
   * haven't been updated to read the gate keep working; production call
   * sites should always pass the real value. */
  isEhrSyncCleared?: boolean;
  /** INT-36: route this import through the org-wide EHR session rather
   * than a per-provider workflow signal. True only for CentralReach orgs
   * on shared credentials (`isCentralReachUser && isOrgUsernamePasswordOrg`).
   *
   * Passed in rather than read from AdminDataProvider so this hook stays
   * usable — and testable — outside that provider. Defaults to false so
   * any call site not yet updated keeps the legacy behaviour. */
  usesOrgSession?: boolean;
}

export const useClientListImport = (options: UseClientListImportOptions = {}) => {
  const {
    enabled = true,
    errorMessage = "Error importing client list",
    waitForStatusTransition = true,
    onSuccess,
    onError,
    onLoginFailed,
    isEhrSyncCleared = true,
    usesOrgSession = false,
  } = options;

  const [isPolling, setIsPolling] = useState(false);
  const [clientsImportPending, setClientsImportPending] = useState(false);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const maxPollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousStatusRef = useRef<EhrConnectionStatus | null | undefined>(undefined);
  const sawImportingStatusRef = useRef(false);
  // Set eagerly whenever ImportingClientList → Connected is observed, even before polling
  // starts. Needed because a fast import can complete during the 5s init delay, advancing
  // previousStatusRef to Connected before isPolling is true.
  const sawDirectTransitionRef = useRef(false);
  // Set whenever Reconnecting or LoggingIn is observed — both signal a session refresh
  // (backend auto-reconnect or re-login), the same pairing EHRConnectionProvider treats
  // identically for its analogous auto-import success guard. The setter below isn't gated
  // on isPolling/clientsImportPending, but is reset at both import-start and import-end
  // boundaries (and on each fresh ImportingClientList sighting, see below), so it's
  // effectively scoped to "since this import's last ImportingClientList sighting." Only when
  // it's true do we require the literal direct-transition proof below; otherwise a
  // missed/throttled poll (e.g. a backgrounded tab) that skips straight past the exact
  // ImportingClientList → Connected edge would strand the state machine until the 120s
  // failsafe fires a false error toast, even though nothing resembling a session refresh
  // ever happened.
  const sawSessionRefreshRef = useRef(false);
  // Counts consecutive ConnectionError observations so a single transient flap
  // during a Temporal retry doesn't fire the error toast prematurely.
  const connectionErrorStreakRef = useRef(0);
  // `dataUpdatedAt` of the last fetch we counted toward the streak. Required
  // because (a) React-Query's default `structuralSharing` preserves the data
  // reference across polls when content is unchanged, so the effect's
  // `ehrConnectionData` dep wouldn't re-fire on a second identical
  // ConnectionError, and (b) StrictMode (or any other same-poll effect re-run)
  // would otherwise bump the streak twice from one fetch and fire after a
  // single transient observation. Gating on the fetch timestamp covers both.
  const lastCountedConnectionErrorDataUpdatedAtRef = useRef<number>(0);
  // Symmetric pair for the Connected debounce: counts consecutive Connected
  // observations after we've seen evidence the workflow advanced past login,
  // gated on the per-fetch dataUpdatedAt for the same reasons.
  const connectedAfterImportingStreakRef = useRef(0);
  const lastCountedConnectedDataUpdatedAtRef = useRef<number>(0);
  // Mirrors "is there an import actively polling right now", read (never captured in a
  // closure) inside the visibilitychange handler below so it always reflects the latest
  // value regardless of when that handler happens to fire relative to a React re-render.
  const isActiveImportRef = useRef(false);

  // Use refs to stabilize callbacks and values without adding them to dependency arrays
  // Assign directly during render for synchronous updates (no stale values between renders)
  const errorMessageRef = useRef(errorMessage);
  errorMessageRef.current = errorMessage;
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onLoginFailedRef = useRef(onLoginFailed);
  onLoginFailedRef.current = onLoginFailed;
  const waitForStatusTransitionRef = useRef(waitForStatusTransition);
  waitForStatusTransitionRef.current = waitForStatusTransition;
  const isEhrSyncClearedRef = useRef(isEhrSyncCleared);
  isEhrSyncClearedRef.current = isEhrSyncCleared;

  // Every branch that shows the "Error importing client list" toast logs the same
  // structured context to Datadog first. Before this, several notifyError()-only
  // branches (CredentialsFailed/ConnectionError, active and resumed, plus the 120s
  // failsafe) had zero corresponding RUM error — the original PROA-1742 investigation
  // found the toast with no matching Datadog event, which made root-causing take far
  // longer than it should have. Reads only refs/params (never component state directly),
  // so it's safe to call from any closure in this hook regardless of when that closure
  // was created — the refs always reflect their latest value.
  const logAndNotifyImportError = (reason: string, status: EhrConnectionStatus | null | undefined) => {
    datadogRum.addError(`Client list import failed: ${reason}`, {
      status,
      sawImportingStatus: sawImportingStatusRef.current,
      sawSessionRefresh: sawSessionRefreshRef.current,
      sawDirectTransition: sawDirectTransitionRef.current,
      connectedStreak: connectedAfterImportingStreakRef.current,
      connectionErrorStreak: connectionErrorStreakRef.current,
    });
    notifyError(errorMessageRef.current);
  };

  const { mutateAsync: importClients } = useImportClientListMutation();
  const { queueAndWait, cancel: cancelOrgSessionPoll } = useOrgSessionAction();

  const {
    data: ehrConnectionData,
    refetch: refetchEhrConnection,
    dataUpdatedAt: ehrConnectionDataUpdatedAt,
  } = useEhrConnectionQuery(
    {},
    {
      enabled,
      gcTime: 0,
      staleTime: 0,
      refetchInterval: (query) => {
        if (query.state.data?.ehrConnection?.status === EhrConnectionStatus.ImportingClientList || isPolling) {
          return EHR_CONNECTION_POLLING_INTERVAL_MS;
        }
        return false;
      },
    },
  );

  const resetClientImportState = useCallback(() => {
    // Also stops an in-flight org-session poll; without this, cancel
    // would leave the loop running and fire onSuccess later.
    cancelOrgSessionPoll();
    setClientsImportPending(false);
    setIsPolling(false);
    isActiveImportRef.current = false;
    sawImportingStatusRef.current = false;
    sawDirectTransitionRef.current = false;
    sawSessionRefreshRef.current = false;
    connectionErrorStreakRef.current = 0;
    lastCountedConnectionErrorDataUpdatedAtRef.current = 0;
    connectedAfterImportingStreakRef.current = 0;
    lastCountedConnectedDataUpdatedAtRef.current = 0;
    previousStatusRef.current = undefined;
    if (maxPollingTimeoutRef.current) {
      clearTimeout(maxPollingTimeoutRef.current);
      maxPollingTimeoutRef.current = null;
    }
  }, [cancelOrgSessionPoll]);

  const clearPollingTimeout = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, []);

  // Fail-safe: if polling runs too long without resolution, error out. But first re-check
  // whether we're actually sitting on a legitimate completed import — e.g. a backgrounded
  // tab that throttled refetchInterval enough that the CONNECTED_CONFIRMATION_POLLS streak
  // never got a chance to accumulate, even though the import genuinely finished.
  // previousStatusRef always holds the most recently observed status (updated at the end of
  // every poll's effect run), so it's safe to read here despite this callback being
  // scheduled long before it fires. Reuses the same eligibility rule as
  // handleConnectedActive's gate.
  //
  // Extracted so the Reconnecting handler below can reschedule this timer (give an active
  // auto-reconnect/retry cycle a fresh window) rather than letting a fixed wall-clock
  // deadline expire mid-retry — see PROA-1742 Datadog evidence: a real successful HR
  // ImportingClientList -> Reconnecting -> Reconnecting -> Connected -> ImportingClientList
  // -> Connected recovery took ~7 minutes, well past the original 120s cap.
  //
  // Every caller of this function (the initial arm in startImport, the Reconnecting
  // reschedule, and the visibility-regain rearm below) can run while the tab is already
  // hidden — e.g. the user clicks Refresh and immediately switches tabs before the 5s
  // init delay elapses. The visibilitychange handler below only pauses the timer on a
  // hidden *transition*; it has nothing to react to if the tab was hidden before the
  // timer was ever armed. Without this check, the 120s deadline ticks down entirely in
  // the background — invisible to the pause guard — and fires a false-positive error
  // even when the import completes normally (see PROA-2041, provider_397_HI_RASMUS,
  // 2026-08-06T07:25:17: backend settled into CONNECTED within 52s, but the tab was
  // foregrounded for only ~8s of the session and the failsafe still fired at 120s).
  const scheduleMaxPollingTimeout = useCallback(() => {
    if (maxPollingTimeoutRef.current) {
      clearTimeout(maxPollingTimeoutRef.current);
      maxPollingTimeoutRef.current = null;
    }
    if (document.visibilityState === "hidden") {
      // Leave it unarmed. The visibilitychange handler's "visible" branch calls this
      // function again once the tab is actually foregrounded, arming a fresh window then.
      return;
    }
    maxPollingTimeoutRef.current = setTimeout(() => {
      const isEligibleConnected =
        previousStatusRef.current === EhrConnectionStatus.Connected &&
        (!sawImportingStatusRef.current || !sawSessionRefreshRef.current || sawDirectTransitionRef.current);
      if (isEligibleConnected) {
        onSuccessRef.current?.();
        resetClientImportState();
        return;
      }
      logAndNotifyImportError("120s polling timeout", previousStatusRef.current);
      onErrorRef.current?.(new Error("Import timed out"));
      resetClientImportState();
    }, MAX_POLLING_DURATION_MS);
  }, [resetClientImportState]);

  // Monitor EHR connection status for import completion
  useEffect(() => {
    const status = ehrConnectionData?.ehrConnection?.status;

    if (status === EhrConnectionStatus.ImportingClientList) {
      sawImportingStatusRef.current = true;
      // A fresh ImportingClientList sighting means real import progress is happening right
      // now — any earlier session-refresh detour (including one observed before the import
      // ever reached ImportingClientList, e.g. a normal pre-import auto-reconnect) no longer
      // bears on whether *this* Connected, once reached, represents true completion.
      sawSessionRefreshRef.current = false;
      // Same reasoning applies to a stale direct-transition proof: a transient Connected
      // flap earlier in the same import attempt (before this fresh ImportingClientList) must
      // not be reused as "proof" for whatever Connected comes after this point — otherwise a
      // later genuine session-refresh detour could still slip past the eligibility gate below
      // on the strength of that unrelated, earlier flap.
      sawDirectTransitionRef.current = false;
    }

    if (status === EhrConnectionStatus.Reconnecting || status === EhrConnectionStatus.LoggingIn) {
      sawSessionRefreshRef.current = true;
    }

    // Reset the ConnectionError streak whenever we observe a non-error status,
    // so a stale single observation doesn't combine with a later one to fire prematurely.
    if (status !== undefined && status !== EhrConnectionStatus.ConnectionError) {
      connectionErrorStreakRef.current = 0;
      lastCountedConnectionErrorDataUpdatedAtRef.current = 0;
    }

    // Symmetric reset for the Connected debounce: any non-Connected observation
    // (e.g. flap back to Importing during a Temporal retry) breaks the streak.
    if (status !== undefined && status !== EhrConnectionStatus.Connected) {
      connectedAfterImportingStreakRef.current = 0;
      lastCountedConnectedDataUpdatedAtRef.current = 0;
    }

    // Track a direct ImportingClientList → Connected transition as soon as it is observed,
    // even before isPolling is true. A fast import can complete during the 5s init delay,
    // advancing previousStatusRef to Connected before polling starts — if we only check
    // previousStatusRef inside the isPolling guard we would miss it.
    if (
      status === EhrConnectionStatus.Connected &&
      previousStatusRef.current === EhrConnectionStatus.ImportingClientList
    ) {
      sawDirectTransitionRef.current = true;
    }

    // Handles status changes while an import triggered in this session is in progress.
    // Treat a stable Connected streak (see CONNECTED_CONFIRMATION_POLLS below) as success
    // unless we saw a session-refresh (Reconnecting or LoggingIn) detour without a
    // subsequent direct ImportingClientList → Connected transition — that specific path
    // means the import was not actually completed, so we keep polling instead of showing a
    // false-success toast.
    //
    // Each status gets its own handler — keeps cognitive complexity per
    // function low and isolates the debounce arithmetic from the dispatcher.
    function handleConnectedActive() {
      // Only require the literal direct-transition proof when a session-refresh
      // (Reconnecting/LoggingIn) detour was actually observed — that's the one path where a
      // stable Connected streak can be reached without the import having truly completed
      // (see the session-refresh-detour explanation above). Absent that, a stable Connected
      // streak (checked below) is sufficient even if the exact adjacent poll edge was missed.
      if (sawImportingStatusRef.current && sawSessionRefreshRef.current && !sawDirectTransitionRef.current) {
        return; // Eligibility not met — no workflow advancement observed yet.
      }
      // Require N consecutive Connected polls so a transient Connected flap
      // between Temporal retry attempts doesn't fire onSuccess prematurely.
      // Gate on dataUpdatedAt for the same reasons as the ConnectionError
      // debounce: structuralSharing + same-poll effect re-runs.
      if (ehrConnectionDataUpdatedAt !== lastCountedConnectedDataUpdatedAtRef.current) {
        connectedAfterImportingStreakRef.current += 1;
        lastCountedConnectedDataUpdatedAtRef.current = ehrConnectionDataUpdatedAt;
      }
      if (connectedAfterImportingStreakRef.current < CONNECTED_CONFIRMATION_POLLS) {
        return;
      }
      onSuccessRef.current?.();
      resetClientImportState();
    }

    function handleCredentialsFailedActive() {
      // CredentialsFailed can happen before ImportingClientList (auto-reconnect login
      // failure), so don't require sawImportingStatusRef here.
      // Only show error toast if no onLoginFailed handler will open a recovery dialog.
      if (!onLoginFailedRef.current) {
        logAndNotifyImportError("CredentialsFailed (active import)", status);
      }
      onLoginFailedRef.current?.();
      onErrorRef.current?.(new Error(`Import failed: EHR status is ${status}`));
      resetClientImportState();
    }

    function handleConnectionErrorActive() {
      // Infrastructure failure — credentials are valid; user retries via the badge.
      // Require N consecutive ConnectionError polls so a single transient flap during
      // a Temporal retry doesn't fire a false error toast that gets contradicted by
      // a subsequent successful attempt. Gate the increment on dataUpdatedAt to
      // handle (a) structuralSharing-preserved data references and (b) StrictMode-
      // style same-poll re-runs.
      if (ehrConnectionDataUpdatedAt !== lastCountedConnectionErrorDataUpdatedAtRef.current) {
        connectionErrorStreakRef.current += 1;
        lastCountedConnectionErrorDataUpdatedAtRef.current = ehrConnectionDataUpdatedAt;
      }
      if (connectionErrorStreakRef.current < CONNECTION_ERROR_CONFIRMATION_POLLS) {
        return;
      }
      logAndNotifyImportError("ConnectionError (active import)", status);
      onErrorRef.current?.(new Error(`Import failed: EHR status is ${status}`));
      resetClientImportState();
    }

    function handleReconnectingActive() {
      // The backend is actively auto-reconnecting/retrying rather than failing outright —
      // give it a fresh window instead of letting a fixed wall-clock deadline expire
      // mid-retry. See the scheduleMaxPollingTimeout comment for the Datadog evidence
      // (PROA-1742) motivating this: a real successful recovery took ~7 minutes.
      scheduleMaxPollingTimeout();
    }

    function handleDisconnectedActive() {
      if (!sawImportingStatusRef.current) return;
      // Don't fire the "Error importing client list" toast on Disconnected —
      // it's most commonly user-initiated (clicking Disconnect in the EHR
      // badge), and the badge UI itself visibly switches to "EHR disconnected".
      // Showing an import-error toast in that flow is misleading. Genuinely
      // unexpected drops are covered by the ConnectionError and
      // CredentialsFailed branches, which keep their toasts.
      onErrorRef.current?.(new Error(`Import interrupted: EHR status is ${status}`));
      resetClientImportState();
    }

    function handleActiveImportStatus() {
      switch (status) {
        case EhrConnectionStatus.Connected:
          handleConnectedActive();
          break;
        case EhrConnectionStatus.CredentialsFailed:
          handleCredentialsFailedActive();
          break;
        case EhrConnectionStatus.ConnectionError:
          handleConnectionErrorActive();
          break;
        case EhrConnectionStatus.Reconnecting:
          handleReconnectingActive();
          break;
        case EhrConnectionStatus.Disconnected:
          handleDisconnectedActive();
          break;
      }
    }

    // Handles status changes for an import that started before this page load
    // (e.g. the user refreshed while an import was in progress).
    function handleResumedImportStatus() {
      if (status === EhrConnectionStatus.Connected) {
        onSuccessRef.current?.();
      } else if (status === EhrConnectionStatus.CredentialsFailed) {
        if (!onLoginFailedRef.current) {
          logAndNotifyImportError("CredentialsFailed (resumed import)", status);
        }
        onLoginFailedRef.current?.();
        onErrorRef.current?.(new Error(`Import failed: EHR status is ${status}`));
      } else if (status === EhrConnectionStatus.ConnectionError) {
        logAndNotifyImportError("ConnectionError (resumed import)", status);
        onErrorRef.current?.(new Error(`Import failed: EHR status is ${status}`));
      } else if (status === EhrConnectionStatus.Disconnected) {
        // Mirror handleDisconnectedActive: skip the misleading
        // "Error importing client list" toast on Disconnected since the
        // most common trigger is a user-initiated Disconnect from the badge.
        onErrorRef.current?.(new Error(`Import interrupted: EHR status is ${status}`));
      }
    }

    if (clientsImportPending && isPolling) {
      handleActiveImportStatus();
    } else if (
      !clientsImportPending &&
      previousStatusRef.current === EhrConnectionStatus.ImportingClientList &&
      status !== EhrConnectionStatus.ImportingClientList &&
      status !== undefined
    ) {
      // Resumed import detection (e.g. page refresh during active import)
      handleResumedImportStatus();
    }

    previousStatusRef.current = status;
  }, [
    ehrConnectionData,
    // React-Query's default structuralSharing keeps `ehrConnectionData`'s
    // reference stable across polls when content is unchanged. Without this
    // dep the effect would not re-fire on a second consecutive identical
    // ConnectionError poll and the streak would never reach the threshold.
    ehrConnectionDataUpdatedAt,
    clientsImportPending,
    isPolling,
    resetClientImportState,
    scheduleMaxPollingTimeout,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearPollingTimeout();
      if (maxPollingTimeoutRef.current) {
        clearTimeout(maxPollingTimeoutRef.current);
      }
    };
  }, [clearPollingTimeout]);

  // React-Query's refetchInterval pauses entirely while the tab is backgrounded
  // (refetchIntervalInBackground defaults to false) — so a backgrounded tab doesn't just
  // throttle polling, it stops it completely. The fixed-deadline MAX_POLLING_DURATION_MS
  // setTimeout keeps running regardless (background timers are clamped, not paused), so it
  // can fire a false "Error importing client list" toast for an import that was still
  // quietly succeeding the whole time the tab was hidden — the reschedule-on-Reconnecting
  // guard above can't help here because there were no polls to observe Reconnecting on.
  // See PROA-2075/PROA-2076 (provider_397_HI_RASMUS..., 2026-08-05T14:00:32): the tab was
  // backgrounded for ~4 minutes spanning the failsafe deadline while the backend
  // auto-reconnected and the import genuinely completed.
  //
  // Pausing (clearing) the failsafe the instant the tab goes hidden — rather than merely
  // rescheduling it once visibility returns — means it can never become overdue while
  // hidden, so there's no race between an already-queued/firing timeout callback and this
  // handler's re-arm call. On visibility regain, force an immediate status check and give
  // the failsafe a fresh window, but only if an import is genuinely still active —
  // `isActiveImportRef` is read fresh on every invocation (never captured at effect-setup
  // time), so a failsafe/success resolution that lands in the gap between the tab going
  // hidden and coming back visible is respected instead of this handler racing it and
  // scheduling an orphaned timer that nothing would otherwise clear.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        if (maxPollingTimeoutRef.current) {
          clearTimeout(maxPollingTimeoutRef.current);
          maxPollingTimeoutRef.current = null;
        }
        return;
      }
      if (document.visibilityState === "visible" && isActiveImportRef.current) {
        refetchEhrConnection();
        scheduleMaxPollingTimeout();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [refetchEhrConnection, scheduleMaxPollingTimeout]);

  /** Queue IMPORT_CLIENTS on the org session and poll the request row.
   *
   * Completion comes from EhrActionRequest, not ehrConnection.status: the
   * org session never writes a connection status (that is keyed on a
   * ScraperSessionWorkflow it does not create), so the transition-based
   * poll would see a steady CONNECTED and report success before the
   * scrape had run.
   *
   * A request that was already in flight (`created: false`) is polled the
   * same way — the sync is org-global, so someone else's run satisfies
   * this caller too.
   */
  const runOrgSessionImport = useCallback(async (): Promise<boolean> => {
    setClientsImportPending(true);
    try {
      const outcome = await queueAndWait({ actionType: "IMPORT_CLIENTS" });
      if (outcome.status === "CANCELLED") return false;
      if (outcome.status === "SUCCEEDED") {
        onSuccessRef.current?.();
        resetClientImportState();
        return true;
      }
      // The request row carries the real reason — rejected credentials, an
      // MFA-gated account, an unreachable EHR. Prefer it over generic copy.
      throw new Error(outcome.status === "TIMED_OUT" ? "Import timed out" : outcome.error ?? errorMessageRef.current);
    } catch (error) {
      notifyError(error instanceof Error ? error.message : errorMessageRef.current);
      // Every onLoginFailed handler invalidates ehrConnection for an
      // org-level account (none of them open the per-provider dialog for
      // one). Without it the badge keeps the status it was polling before
      // — and the org-session poll stops at CONNECTED, so a credential or
      // MFA failure would never reach it. The toast stays: unlike the
      // per-provider path, the request row carries the real reason.
      onLoginFailedRef.current?.();
      onErrorRef.current?.(error);
      resetClientImportState();
      return false;
    }
  }, [errorMessageRef, queueAndWait, resetClientImportState]);

  const startImport = useCallback(async (): Promise<boolean> => {
    if (!isEhrSyncClearedRef.current) {
      onErrorRef.current?.(new Error("EHR sync is not yet cleared for this organization"));
      return false;
    }
    if (usesOrgSession) {
      datadogRum.addAction("import-client-list-org-session");
      return runOrgSessionImport();
    }
    try {
      datadogRum.addAction("import-client-list");
      sawImportingStatusRef.current = false;
      sawDirectTransitionRef.current = false;
      sawSessionRefreshRef.current = false;
      connectionErrorStreakRef.current = 0;
      setClientsImportPending(true);
      await importClients({});

      // API-based EHRs still status-masked by the backend (currently only Hipp
      // Health — Passage Health and Therapz moved off this branch once the
      // backend started reporting real status, PROA-2515) don't transition
      // through IMPORTING_CLIENT_LIST, so skip polling and treat mutation
      // success as import success.
      if (!waitForStatusTransitionRef.current) {
        onSuccessRef.current?.();
        resetClientImportState();
        return true;
      }

      // Wait 5 seconds before polling to let the underlying workflow action start and update the status
      pollingTimeoutRef.current = setTimeout(async () => {
        let transientRefetchError: unknown;
        try {
          // refetch status before enabling polling
          // to make sure that fresh status is used in the following checks
          // react-query's refetch() swallows the underlying fetch error and resolves
          // normally unless throwOnError is explicitly requested — pass it so a real
          // network failure actually reaches this catch instead of silently resolving.
          try {
            await refetchEhrConnection({ throwOnError: true });
          } catch (error) {
            // A single transient failure here (e.g. a dev network blip) shouldn't be
            // fatal — retry once immediately before giving up, rather than toasting on
            // one flaky observation. Unlike the ConnectionError streak debounce
            // elsewhere in this hook, this is a one-shot retry with no counter/threshold.
            transientRefetchError = error;
            datadogRum.addError("Transient error refetching EHR connection before polling; retrying once", {
              error,
            });
            await refetchEhrConnection({ throwOnError: true });
          }
          setIsPolling(true);
          isActiveImportRef.current = true;
          scheduleMaxPollingTimeout();
        } catch (error) {
          // If the retry also failed, correlate it with the first attempt's error here —
          // otherwise the first failure (potentially more diagnostic, e.g. an auth error
          // vs. a generic timeout on retry) is only ever visible as a separate breadcrumb.
          datadogRum.addError("Error during polling", { error, transientRefetchError });
          notifyError(errorMessageRef.current);
          resetClientImportState();
          onErrorRef.current?.(error);
        }
      }, CLIENT_IMPORT_INITIALIZATION_DELAY_MS);
      return true;
    } catch (error) {
      datadogRum.addError("Error importing client list", { error });
      notifyError(errorMessageRef.current);
      resetClientImportState();
      onErrorRef.current?.(error);
      return false;
    }
  }, [
    importClients,
    refetchEhrConnection,
    resetClientImportState,
    scheduleMaxPollingTimeout,
    runOrgSessionImport,
    usesOrgSession,
  ]);

  const cancelImport = useCallback(() => {
    clearPollingTimeout();
    resetClientImportState();
  }, [clearPollingTimeout, resetClientImportState]);

  return {
    isImporting:
      clientsImportPending || ehrConnectionData?.ehrConnection?.status === EhrConnectionStatus.ImportingClientList,
    startImport,
    cancelImport,
    ehrConnectionStatus: ehrConnectionData?.ehrConnection?.status,
  };
};
